/**
 * GoldChampionMarquee — Feb 2026
 * ==============================
 *
 * Slim animated pill that surfaces the most recent Gold Proactive
 * Champion right at the top of every teen's Dashboard. Auto-hides
 * 48 hours after the award drop so the surface stays fresh — you only
 * see this week's Gold winner, never a stale champion from a fortnight
 * ago. Dismissible; the dismissal is stored per-award-key in
 * localStorage so a teen who X'es it doesn't see it again for THAT
 * champion, but does see the next Gold winner.
 *
 * Backend: `GET /api/proactivity/podium?period=week` (masked payload).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, X } from 'lucide-react';
import { apiClient } from '../../api/client';

const HIDE_AFTER_MS = 48 * 60 * 60 * 1000; // 48h

const parseAwardedMs = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

const dismissKeyFor = (periodKey) => `hibff.gold_champion.dismissed.${periodKey || 'unknown'}`;

const GoldChampionMarquee = () => {
  const [gold, setGold] = useState(null);      // { first_name, nft_count, awarded_at, period_key }
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.request('/api/proactivity/podium?period=week');
        if (cancelled) return;
        const g = (r?.entries || []).find(e => e.place === 1);
        if (!g) return;
        const key = r?.period_key || 'unknown';
        // Respect prior dismissal for this exact champion.
        try {
          if (localStorage.getItem(dismissKeyFor(key)) === '1') {
            setDismissed(true);
          }
        } catch (e) { /* localStorage blocked — treat as fresh */ }
        setGold({ ...g, period_key: key });
      } catch (e) {
        // silent — dashboard survives a podium fetch failure
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ticks once per minute so the 48h expiry is honoured even if the
  // dashboard has been open for hours.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const expired = useMemo(() => {
    const ms = parseAwardedMs(gold?.awarded_at);
    if (ms == null) return false; // if the backend didn't return awarded_at, keep showing
    return (now - ms) > HIDE_AFTER_MS;
  }, [gold, now]);

  if (!gold || dismissed || expired) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(dismissKeyFor(gold.period_key), '1'); } catch (err) { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="mb-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-full shadow-sm border animate-fadeIn"
      style={{
        background: 'linear-gradient(90deg, #FEF3C7 0%, #FDE68A 50%, #FEF3C7 100%)',
        backgroundSize: '200% 100%',
        borderColor: '#F59E0B',
        animation: 'fadeIn 0.4s ease-out, goldShimmer 6s linear infinite',
      }}
      data-testid="gold-champion-marquee"
    >
      <style>{`
        @keyframes goldShimmer {
          0%   { background-position:   0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position:   0% 50%; }
        }
      `}</style>
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-lg" aria-hidden>🥇</span>
        <Trophy size={14} className="text-amber-700 flex-shrink-0" />
        <span className="text-sm font-semibold text-amber-900 truncate">
          <strong className="text-amber-950">{gold.first_name}</strong>
          {' is this week\u2019s '}
          <span className="font-bold">Gold Champion</span>
          {gold.nft_count ? ` · ${gold.nft_count} NFTs` : ''}
        </span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="w-6 h-6 rounded-full flex items-center justify-center text-amber-800 hover:bg-amber-200/70 transition flex-shrink-0"
        aria-label="Dismiss"
        data-testid="gold-champion-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default GoldChampionMarquee;
